import { getFullName } from "@vega-api/custom";
import type { InvitationCheckResponse } from "@vega-api/generated/types/invitationCheckResponse";
import { UserActivationType } from "@vega-api/generated/types/userActivationType";
import { m } from "@vega-core/paraglide/messages";

export type ActivationFlowConfig = {
  heading: string;
  body: string;
  primaryAction?: {
    label: string;
    dataTestId: string;
    onClick: () => void;
  };
  /** When false, the primary action button renders without an icon. Default: true (shows IDP icon) */
  showPrimaryActionIcon?: boolean;
};

export type ActivationFlowConfigOptions = {
  redirectUrl?: string;
  invitationCheckData?: InvitationCheckResponse;
  companyName?: string;
  onSignIn?: () => void;
};

/**
 * Returns the configuration for the activation success screen based on UserActivationType.
 * Scalable: add new cases for LINK, EMAIL, SMS, etc. as needed.
 */
export function getActivationFlowConfig(
  userActivationType: UserActivationType | undefined,
  options: ActivationFlowConfigOptions,
): ActivationFlowConfig {
  const {
    redirectUrl,
    invitationCheckData,
    companyName = "",
    onSignIn,
  } = options;

  const hasInviterName =
    invitationCheckData &&
    (invitationCheckData.invitingUserFirstName ||
      invitationCheckData.invitingUserLastName);
  const inviterName = hasInviterName
    ? getFullName({
        firstName: invitationCheckData.invitingUserFirstName ?? "",
        lastName: invitationCheckData.invitingUserLastName ?? "",
      })
    : companyName;

  switch (userActivationType) {
    case UserActivationType.EMAIL:
      return {
        heading: m.auth_checkYourEmail(),
        body: m.auth_temporaryPasswordSentMessage(),
        showPrimaryActionIcon: false,
        primaryAction:
          redirectUrl || onSignIn
            ? {
                label: m.btn_signIn(),
                dataTestId: "sign-in",
                onClick: redirectUrl
                  ? () => {
                      window.location.href = redirectUrl;
                    }
                  : onSignIn!,
              }
            : undefined,
      };

    case UserActivationType.TEMPORARY_PASSWORD:
    case UserActivationType.LINK:
    case UserActivationType.SMS:
    case UserActivationType.ALREADY_ACTIVATED:
      return {
        heading: m.auth_createYourAccountWith(),
        body: m.auth_youveBeenInvitedBy({
          companyName: companyName,
          name: inviterName || companyName,
        }),
        primaryAction: redirectUrl
          ? {
              label: m.auth_continue_with_okta(),
              dataTestId: "continue-with-idp",
              onClick: () => {
                window.location.href = redirectUrl;
              },
            }
          : undefined,
      };

    case UserActivationType.HANDLED_BY_IDP:
    default:
      return {
        heading: m.auth_createYourAccountWith(),
        body: m.auth_youveBeenInvitedBy({
          companyName: companyName,
          name: inviterName || companyName,
        }),
        primaryAction: redirectUrl
          ? {
              label: m.auth_continue_with_okta(),
              dataTestId: "continue-with-okta",
              onClick: () => {
                window.location.href = redirectUrl;
              },
            }
          : undefined,
      };
  }
}
